import { useState, type KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import { longDate, niceScale, shortDate, type DailyPoint } from './chart-utils'

/**
 * Biểu đồ cột theo ngày.
 *
 * - Một chuỗi số liệu nên không có hộp chú thích: tiêu đề khối đã nói đang vẽ gì.
 * - Chỉ ghi số lên hai cột: cao nhất và hôm nay. Giá trị còn lại xem bằng
 *   cách rê chuột / chạm, dùng phím ← → khi đang focus, hoặc đọc bảng ẩn cho
 *   trình đọc màn hình.
 * - Cả chiều cao của một ngày là vùng chạm, không chỉ phần cột được tô.
 */
export function DailyBars({
  data,
  format,
  tickFormat = format,
  label,
  integer = false,
  height = 200,
  className,
}: {
  data: DailyPoint[]
  /** Định dạng giá trị trong tooltip và nhãn trên cột. */
  format: (n: number) => string
  /** Định dạng nhãn trục y (thường rút gọn hơn). */
  tickFormat?: (n: number) => string
  /** Tên số liệu, dùng cho aria-label và bảng ẩn. */
  label: string
  integer?: boolean
  height?: number
  className?: string
}) {
  const [active, setActive] = useState<number | null>(null)
  const n = data.length
  const maxVal = Math.max(0, ...data.map((d) => d.value))
  const scale = niceScale(maxVal, 4, integer)
  const maxIdx = maxVal > 0 ? data.findIndex((d) => d.value === maxVal) : -1
  const todayIdx = n - 1
  const labelEvery = Math.max(1, Math.ceil(n / 6))
  const pct = (v: number) => (scale.max > 0 ? (v / scale.max) * 100 : 0)

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    setActive((cur) => {
      const base = cur ?? todayIdx
      return Math.min(n - 1, Math.max(0, base + (e.key === 'ArrowRight' ? 1 : -1)))
    })
  }

  const total = data.reduce((s, d) => s + d.value, 0)

  return (
    <div className={cn('grid grid-cols-[auto_minmax(0,1fr)] gap-x-2', className)}>
      {/* Trục y */}
      <div className="relative w-11 text-right" style={{ height }} aria-hidden>
        {scale.ticks.map((t) => (
          <span
            key={t}
            className="absolute right-0 translate-y-1/2 text-[11px] leading-none tabular-nums text-muted"
            style={{ bottom: `${pct(t)}%` }}
          >
            {tickFormat(t)}
          </span>
        ))}
      </div>

      {/* Vùng vẽ */}
      <div
        className="relative outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-raise rounded-sm"
        style={{ height }}
        tabIndex={0}
        role="img"
        aria-label={`${label} ${n} ngày, tổng ${format(total)}. Dùng phím mũi tên để xem từng ngày.`}
        onKeyDown={onKey}
        onBlur={() => setActive(null)}
        onPointerLeave={() => setActive(null)}
      >
        {scale.ticks.map((t) => (
          <span
            key={t}
            className={cn('absolute inset-x-0 h-px', t === 0 ? 'bg-rule' : 'bg-[rgb(var(--c-grid))]')}
            style={{ bottom: `${pct(t)}%` }}
            aria-hidden
          />
        ))}

        <div className="absolute inset-0 flex items-stretch gap-[2px]">
          {data.map((d, i) => {
            const isToday = i === todayIdx
            const isActive = i === active
            const showValue = d.value > 0 && (i === maxIdx || isToday) && active === null
            return (
              <div
                key={d.date}
                className="relative flex min-w-0 flex-1 items-end justify-center"
                onPointerEnter={() => setActive(i)}
                onPointerDown={() => setActive(i)}
              >
                <span
                  className={cn(
                    'relative block w-full max-w-[24px] rounded-t-[4px] transition-[filter,background-color] duration-150',
                    isToday ? 'bg-accent' : 'bg-[rgb(var(--c-bar))]',
                    isActive && (isToday ? 'brightness-110' : 'bg-accent/45'),
                  )}
                  style={{ height: `${Math.max(pct(d.value), d.value > 0 ? 1.5 : 0)}%` }}
                >
                  {showValue && (
                    <span
                      className={cn(
                        'absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold tabular-nums',
                        isToday ? 'text-accent' : 'text-inkSoft',
                      )}
                    >
                      {format(d.value)}
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>

        {active !== null && data[active] && (
          <div
            className="pointer-events-none absolute z-10 rounded-lg bg-ink px-2.5 py-1.5 text-xs text-raise shadow-pop"
            style={{
              left: `${((active + 0.5) / n) * 100}%`,
              bottom: `calc(${Math.min(pct(data[active].value), 88)}% + 10px)`,
              transform: `translateX(${active < n * 0.15 ? '-15%' : active > n * 0.85 ? '-85%' : '-50%'})`,
            }}
            aria-hidden
          >
            <div className="whitespace-nowrap text-sm font-semibold tabular-nums">{format(data[active].value)}</div>
            <div className="whitespace-nowrap opacity-75">
              {active === todayIdx ? 'Hôm nay' : longDate(data[active].date)}
            </div>
          </div>
        )}
      </div>

      {/* Trục x */}
      <div />
      <div className="mt-2 flex gap-[2px]" aria-hidden>
        {data.map((d, i) => {
          const show = i === todayIdx || (i % labelEvery === 0 && todayIdx - i >= labelEvery / 2)
          return (
            <span
              key={d.date}
              className={cn(
                'min-w-0 flex-1 overflow-visible whitespace-nowrap text-center text-[11px] leading-none',
                i === todayIdx ? 'font-semibold text-ink' : 'text-muted',
              )}
            >
              {show ? (i === todayIdx ? 'Nay' : shortDate(d.date)) : ''}
            </span>
          )
        })}
      </div>

      <table className="sr-only">
        <caption>{label}</caption>
        <tbody>
          {data.map((d) => (
            <tr key={d.date}>
              <th scope="row">{longDate(d.date)}</th>
              <td>{format(d.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
